import { useQuery } from '@tanstack/react-query';

import { api } from '../../../lib/api-client';

/**
 * Item 481 (achado A-37 da noturna) — os turnos do conductor no Explorador de auditoria.
 *
 * Quando a IA recusa uma ação de risco em nome de terceiro («no nome da Maria da Silva»), a
 * auditoria da conversa só tem `routing` e `knowledge-selected`: a recusa por step-up já
 * gera `identity-verification.step-up {decision:'handoff', action, reason}`, mas a recusa em
 * PROSA (a IA explica e não age) não tem sinal determinístico — o único rastro é o
 * `AIConductorTurn` (ação + justificativa), que não aparecia aqui. Princípio 11.
 *
 * Leitura só: `GET /v1/conversations/:id/conductor-turns` (cap `core-conversation`). Perfil
 * sem a cap (ex.: DPO só com auditoria) recebe 403 — o painel diz isso, sem quebrar a lista.
 */
const ACTION_LABEL: Record<string, string> = {
  reply: 'Respondeu',
  call_tool: 'Chamou ferramenta',
  escalate: 'Escalou',
  wait: 'Aguardou',
};

const STATUS_LABEL: Record<string, string> = {
  pending: 'pendente',
  executed: 'executado',
  failed: 'falhou',
};

export const CONDUCTOR_TURNS_LIMIT = 50;

export function ConductorTurnsPanel({
  conversationId,
}: {
  readonly conversationId: string;
}): JSX.Element {
  const query = useQuery({
    queryKey: ['audit-conductor-turns', conversationId],
    queryFn: () => api.listConductorTurns(conversationId, CONDUCTOR_TURNS_LIMIT),
    retry: false,
    staleTime: 30_000,
  });

  return (
    <section
      aria-label="Turnos da IA"
      className="border-b border-border-subtle bg-surface px-8 py-4"
      data-testid="conductor-turns-panel"
    >
      <h2 className="text-sm font-semibold text-text-primary">
        Turnos da IA nesta conversa
        {query.data !== undefined && (
          <span className="ml-2 text-xs font-normal text-text-secondary">
            {query.data.meta.total} no total · mostrando {query.data.data.length}
          </span>
        )}
      </h2>
      <p className="mt-0.5 text-xs text-text-secondary">
        O que o conductor decidiu a cada turno e por quê — inclusive quando recusou uma ação sem
        gerar evento próprio. Leitura; não altera nada.
      </p>

      {query.isLoading && <p className="mt-3 text-xs text-text-secondary">Carregando turnos…</p>}
      {query.isError && (
        <p className="mt-3 text-xs text-text-secondary">
          Turnos indisponíveis para este perfil — a leitura exige acesso a conversas
          (`core-conversation`). Os eventos de auditoria abaixo seguem disponíveis.
        </p>
      )}
      {query.data?.data.length === 0 && (
        <p className="mt-3 text-xs text-text-secondary">
          Nenhum turno do conductor nesta conversa (a IA não conduziu, ou conduziu antes do registro
          de turnos).
        </p>
      )}
      {query.data !== undefined && query.data.data.length > 0 && (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-text-secondary">
                <th className="py-1 pr-3 font-medium">#</th>
                <th className="py-1 pr-3 font-medium">Quando</th>
                <th className="py-1 pr-3 font-medium">Agente</th>
                <th className="py-1 pr-3 font-medium">Ação</th>
                <th className="py-1 pr-3 font-medium">Estado</th>
                <th className="py-1 font-medium">Justificativa</th>
              </tr>
            </thead>
            <tbody>
              {query.data.data.map((t) => (
                <tr key={t.id} className="border-t border-border-subtle align-top">
                  <td className="py-1.5 pr-3 text-text-secondary">{t.turnNumber}</td>
                  <td className="py-1.5 pr-3 whitespace-nowrap text-text-secondary">
                    {new Date(t.executedAt).toLocaleString('pt-BR')}
                  </td>
                  <td className="py-1.5 pr-3 whitespace-nowrap text-text-secondary">
                    {t.agentId}@{t.agentVersion}
                  </td>
                  <td className="py-1.5 pr-3 whitespace-nowrap text-text-primary">
                    {ACTION_LABEL[t.action] ?? t.action}
                  </td>
                  <td className="py-1.5 pr-3 whitespace-nowrap text-text-secondary">
                    {STATUS_LABEL[t.status] ?? t.status}
                    {t.latencyMs !== null ? ` · ${t.latencyMs} ms` : ''}
                  </td>
                  <td className="py-1.5 text-text-primary">{t.justification}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
